import { Link, useParams } from "react-router-dom";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { ErrorState, Skeleton } from "../components/ui/Feedback";
import { TBody, TD, TH, THead, TR, Table } from "../components/ui/Table";
import { useSession } from "../features/auth/useSession";
import { useProduct } from "../features/products/useProducts";
import { formatMoney, formatNumber, titleCase } from "../lib/format";
import { SellingPlans } from "../features/products/SellingPlans";
import type { LocationInventory, ProductImage } from "../lib/types";

const Gallery = ({ images }: { images: ProductImage[] }) => {
	if (images.length === 0) {
		return (
			<div className="flex h-40 items-center justify-center rounded-md border border-dashed border-line text-xs text-ink-faint">
				No images
			</div>
		);
	}
	return (
		<div className="flex gap-3 overflow-x-auto pb-1">
			{images.map((image) => (
				<img
					key={image.id}
					src={image.url ?? ""}
					alt={image.altText ?? ""}
					loading="lazy"
					className="h-40 w-40 shrink-0 rounded-md border border-line object-cover"
				/>
			))}
		</div>
	);
};

const InventoryCell = ({ inventory }: { inventory: LocationInventory[] }) => {
	if (inventory.length === 0) return <span className="text-xs text-ink-faint">—</span>;
	return (
		<div className="space-y-1">
			{inventory.map((level) => (
				<div key={level.locationId} className="flex items-baseline gap-2 text-xs">
					<span className="text-ink-muted">{level.locationName ?? "Unnamed location"}</span>
					<span className="font-medium text-ink">{formatNumber(level.available)}</span>
					<span className="text-ink-faint">on hand {formatNumber(level.onHand)}</span>
				</div>
			))}
		</div>
	);
};

export const ProductPage = () => {
	const { productId } = useParams();
	const query = useProduct(productId);
	const session = useSession();
	const currency = session.data?.tenant.currencyCode ?? null;

	if (query.isPending) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-8 w-72" />
				<Skeleton className="h-40 w-full" />
				<Skeleton className="h-64 w-full" />
			</div>
		);
	}

	if (query.isError || !query.data) {
		return (
			<ErrorState
				title="Could not load product"
				description={(query.error as Error | null)?.message}
				action={
					<Link to="/products">
						<Button variant="outline" size="sm">
							Back to products
						</Button>
					</Link>
				}
			/>
		);
	}

	const { product, images, variants, sellingPlans } = query.data;

	return (
		<div className="space-y-8">
			<div className="space-y-3">
				<Link to="/products" className="text-xs text-ink-muted underline-offset-4 hover:underline">
					← Products
				</Link>
				<div className="flex flex-wrap items-center gap-3">
					<h1 className="text-xl font-semibold tracking-tight text-ink">
						{product.title ?? "Untitled product"}
					</h1>
					<Badge variant={product.status === "ACTIVE" ? "solid" : "outline"}>
						{titleCase(product.status)}
					</Badge>
				</div>
				<dl className="flex flex-wrap gap-x-8 gap-y-1 text-xs text-ink-muted">
					<div className="flex gap-2">
						<dt>Handle</dt>
						<dd className="text-ink">{product.handle ?? "—"}</dd>
					</div>
					<div className="flex gap-2">
						<dt>Vendor</dt>
						<dd className="text-ink">{product.vendor ?? "—"}</dd>
					</div>
					<div className="flex gap-2">
						<dt>Type</dt>
						<dd className="text-ink">{product.productType ?? "—"}</dd>
					</div>
				</dl>
				{product.tags && product.tags.length > 0 ? (
					<div className="flex flex-wrap gap-1.5">
						{product.tags.map((tag) => (
							<Badge key={tag}>{tag}</Badge>
						))}
					</div>
				) : null}
			</div>

			<SellingPlans plans={sellingPlans} />

			<section className="space-y-3">
				<h2 className="text-sm font-medium text-ink">Media</h2>
				<Gallery images={images} />
			</section>

			<section className="space-y-3">
				<h2 className="text-sm font-medium text-ink">Variants and inventory by location</h2>
				<div className="overflow-hidden rounded-md border border-line">
					<Table>
						<THead>
							<TR>
								<TH>Variant</TH>
								<TH className="w-40">SKU</TH>
								<TH className="w-28">Price</TH>
								<TH className="w-24">Total</TH>
								<TH>Inventory by location</TH>
							</TR>
						</THead>
						<TBody>
							{variants.map((variant) => (
								<TR key={variant.id}>
									<TD className="font-medium">{variant.title ?? "—"}</TD>
									<TD className="text-ink-muted">{variant.sku ?? "—"}</TD>
									<TD>{formatMoney(variant.price, currency)}</TD>
									<TD>{formatNumber(variant.inventoryQuantity)}</TD>
									<TD>
										<InventoryCell inventory={variant.inventory} />
									</TD>
								</TR>
							))}
						</TBody>
					</Table>
				</div>
			</section>
		</div>
	);
};
